<template>
  <div id="lom-events-create">
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-tour name="myTour" :steps="TourSteps" :options="TourOptions"></v-tour>

    <!-- Premium Page Header -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <v-container class="header-container pa-0">
          <v-row class="align-center" no-gutters>
            <!-- Header Icon & Title Section -->
            <v-col cols="12" md="8" class="d-flex align-center pa-6">
              <div class="header-icon-box mr-6">
                <v-icon size="48" color="white" class="icon-animated"
                  >mdi-calendar-multiple-check</v-icon
                >
              </div>
              <div class="header-title-bar">
                <h1 class="header-title">
                  {{ PageInfo.PageTitle || "LOM Events" }}
                </h1>
                <p class="header-subtitle">
                  {{
                    PageInfo.PageDescription || "Create and manage LOM events"
                  }}
                </p>
              </div>
            </v-col>
            <!-- Action Buttons Section (Right Top) -->
            <v-col
              cols="12"
              md="4"
              class="d-flex justify-end align-center pa-6 gap-2 flex-wrap"
            >
              <v-btn
                @click.prevent="backPage"
                class="action-btn"
                icon
                title="Back to previous page"
              >
                <v-icon>mdi-arrow-left-circle</v-icon>
              </v-btn>
              <v-btn
                @click.prevent="refreshPageData"
                class="action-btn"
                icon
                title="Refresh page data"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-btn
                v-if="PageInfo.TourFlag == 1"
                @click.prevent="startTour"
                class="action-btn"
                icon
                title="Start guided tour"
              >
                <v-icon>mdi-lightbulb-on</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-card>

    <v-card-text class="page-content">
      <!-- Welcome Card -->
      <v-card
        elevation="20"
        class="welcome-card mb-6 overflow-hidden"
        v-if="MemeberFullname != ''"
      >
        <div class="welcome-background"></div>
        <v-card-text class="pa-6 welcome-card-content">
          <v-row wrap class="align-center" no-gutters>
            <v-col cols="auto" class="d-flex justify-center mr-2">
              <div class="welcome-icon-box">
                <v-icon size="32" color="white">mdi-account-star</v-icon>
              </div>
            </v-col>
            <v-col cols="auto" class="flex-grow-1">
              <div class="welcome-greeting-text">
                Welcome, <span class="welcome-name">{{ MemeberFullname }}</span>
              </div>
              <div class="welcome-role">
                <v-icon x-small class="role-icon">mdi-briefcase</v-icon>
                {{ CurrentDesignation }}
              </div>
              <div class="welcome-lom">
                <v-icon x-small class="role-icon">mdi-home-city</v-icon>
                {{ LomName }}
              </div>
            </v-col>
            <v-col cols="auto" class="d-flex align-center ml-2">
              <div class="welcome-badge">
                <v-icon size="18" color="#edbe38">mdi-star</v-icon>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Description Card -->
      <v-card
        elevation="8"
        class="description-card mb-6"
        v-if="PageInfo.PageDescription"
      >
        <div class="desc-header">
          <v-icon class="desc-icon">mdi-information-outline</v-icon>
          <h3 class="desc-title">{{ PageInfo.PageDescription }}</h3>
        </div>
        <v-divider class="desc-divider"></v-divider>
        <div class="desc-content">
          <p class="desc-text">
            Fill out all the required fields marked with
            <span class="required-indicator">*</span> to create and submit your
            LOM event. Please ensure all details are accurate before submission.
          </p>
          <div class="desc-features">
            <div class="feature-item">
              <v-icon small class="feature-icon">mdi-check-circle</v-icon>
              <span>Complete event information</span>
            </div>
            <div class="feature-item">
              <v-icon small class="feature-icon">mdi-check-circle</v-icon>
              <span>Upload quality images</span>
            </div>
            <div class="feature-item">
              <v-icon small class="feature-icon">mdi-check-circle</v-icon>
              <span>Add guest details</span>
            </div>
          </div>
        </div>
      </v-card>

      <div class="d-flex justify-content-center mb-3" v-if="LoadingFlag">
        <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
      </div>
    </v-card-text>

    <v-card elevation="1" color="transparent">
      <v-dialog v-model="addFacultyRecordPrompt" persistent max-width="75%">
        <add-event-faculty
          pageTitle="Add Guest Details"
          pageDescription="Add event faculty"
          :addFacultyRecordPrompt="addFacultyRecordPrompt"
          @hideAddFacultyRecordPrompt="addNewFaculty($event)"
          v-if="addFacultyRecordPrompt"
        ></add-event-faculty>
      </v-dialog>
      <v-dialog v-model="addDocumentRecordPrompt" persistent max-width="75%">
        <add-event-document
          pageTitle="Add Event Document"
          pageDescription="Add event document"
          :addDocumentRecordPrompt="addDocumentRecordPrompt"
          @hideAddDocumentRecordPrompt="addNewDocument($event)"
          v-if="addDocumentRecordPrompt"
        ></add-event-document>
      </v-dialog>
      <v-dialog v-model="addChairmanRecordPrompt" persistent max-width="75%">
        <add-event-chairman
          v-if="addChairmanRecordPrompt"
          :showDialog="addChairmanRecordPrompt"
          @hideDialog="hideChairmanDialog($event)"
          pageTitle="Add Event Chairman (Applicable only while hosting Zone / National Events)"
          pageDescription="Add event chairman"
        ></add-event-chairman>
      </v-dialog>
      <v-dialog v-model="addDirectorRecordPrompt" persistent max-width="75%">
        <add-event-director
          v-if="addDirectorRecordPrompt"
          :showDialog="addDirectorRecordPrompt"
          @hideDialog="hideDirectorDialog($event)"
          pageTitle="Add Program Chairman / Director Details"
          pageDescription="Add event director"
        ></add-event-director>
      </v-dialog>
      <v-card-text>
        <v-container>
          <v-form
            ref="form1"
            v-model="valid1"
            lazy-validation
            v-on:submit.prevent="confirmAlert"
          >
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-calendar-clock</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">Date & Host & Venue</h3>
                  <p class="section-subtitle">
                    Configure event timing and location details
                  </p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Fill in the date, time of the event and the venue
                    details.</span
                  >
                </v-tooltip>
              </div>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="6" lg="4" md="4" id="step1">
                    <label class="field-label">
                      <v-icon small class="label-icon">mdi-calendar</v-icon>
                      <span class="text-danger">*</span> Event Day Type
                    </label>
                    <v-select
                      :reduce="(option) => option.value"
                      :loading="DayTypeOptionsLoading"
                      :items="DayTypeOptions"
                      :rules="DayTypeRules"
                      v-model="DayType"
                      required
                      outlined
                      dense
                      color="#3a67b1"
                    >
                      <template v-slot:prepend>
                        <v-icon color="#3a67b1">mdi-calendar</v-icon>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="6" lg="3" md="3" v-if="DayType != ''">
                    <label class="field-label">
                      <v-icon small class="label-icon">mdi-calendar</v-icon>
                      <span class="text-danger">*</span> From Date
                    </label>
                    <!-- <v-datetime-picker
                      v-model="FromDate"
                      :text-field-props="textFieldProps1"
                      :date-picker-props="dateProps1"
                      :time-picker-props="timeProps1"
                      date-format="yyyy-MM-dd"
                      time-format="HH:mm"
                    ></v-datetime-picker> -->
                    <v-dialog
                      ref="dialog1"
                      v-model="DateModal1"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="FromDate"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        >
                          <template v-slot:prepend>
                            <v-icon color="#2B9EB3"> mdi-calendar </v-icon>
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="FromDate"
                        scrollable
                        :min="FromDateMin"
                        :max="FromDateMax"
                        @input="DateModal1 = false"
                      >
                      </v-date-picker>
                    </v-dialog>
                    <!-- <v-menu
                      v-model="DateModal1"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="FromDate"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="FromDate"
                        @input="DateModal1 = false"
                      ></v-date-picker>
                    </v-menu> -->
                  </v-col>
                  <v-col cols="12" sm="6" lg="3" md="3" v-if="DayType == 2">
                    <label class="field-label">
                      <v-icon small class="label-icon">mdi-calendar</v-icon>
                      <span class="text-danger">*</span> To Date
                    </label>
                    <!-- <v-datetime-picker
                      v-model="ToDate"
                      :text-field-props="textFieldProps2"
                      :date-picker-props="dateProps2"
                      :time-picker-props="timeProps2"
                      date-format="yyyy-MM-dd"
                      time-format="HH:mm"
                    ></v-datetime-picker> -->
                    <v-dialog
                      ref="dialog2"
                      v-model="DateModal2"
                      persistent
                      width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="ToDate"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        >
                          <template v-slot:prepend>
                            <v-icon color="#2B9EB3"> mdi-calendar </v-icon>
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="ToDate"
                        scrollable
                        :min="ToDateMin"
                        :max="ToDateMax"
                        @input="DateModal2 = false"
                      >
                      </v-date-picker>
                    </v-dialog>
                  </v-col>
                  <v-col cols="12" sm="12" lg="12" md="12">
                    <v-row>
                      <v-col cols="12" sm="12" md="12" lg="4">
                        <label class="field-label">
                          <v-icon small class="label-icon"
                            >mdi-home-city</v-icon
                          >
                          <span class="text-danger">*</span> Hosted by JCI
                          India?
                        </label>
                        <v-switch
                          v-model="HostType"
                          :label="`${HostType == 1 ? 'Yes' : 'No'}`"
                        ></v-switch>
                      </v-col>
                      <v-col cols="12" sm="6" lg="4" md="6">
                        <label class="field-label">
                          <v-icon size="16" class="label-icon"
                            >mdi-blood-bag</v-icon
                          >
                          <span class="text-danger">*</span>

                          Event Quantity
                        </label>
                        <v-text-field
                          v-model="EventQuantity"
                          :rules="EventQuantityRules"
                          hint="Enter the event quantity"
                          outlined
                          dense
                          type="number"
                        >
                          <template v-slot:prepend>
                            <v-icon color="#0097d7">mdi-blood-bag</v-icon>
                          </template>
                        </v-text-field>
                        <small class="note-text">
                          <v-icon size="14" color="orange"
                            >mdi-information</v-icon
                          >
                          Note: This is blood donation unit
                        </small>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="!HostType">
                        <label class="field-label">
                          <v-icon small class="label-icon">mdi-map</v-icon>
                          <span class="text-danger">*</span> Host Zone
                        </label>
                        <v-autocomplete
                          :reduce="(option) => option.value"
                          :loading="HostZoneCodeOptionsLoading"
                          :items="HostZoneCodeOptions"
                          :rules="HostZoneCodeRules"
                          v-model="HostZoneCode"
                          dense
                          clearable
                          required
                          outlined
                        ></v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="!HostType">
                        <label class="field-label">
                          <v-icon small class="label-icon"
                            >mdi-organization</v-icon
                          >
                          <span class="text-danger">*</span> Host LOM
                        </label>
                        <v-autocomplete
                          :reduce="(option) => option.value"
                          v-model="HostLomCode"
                          :rules="HostLomCodeRules"
                          :items="HostLomCodeOptions"
                          :loading="HostLomCodeOptionsLoading"
                          dense
                          clearable
                          required
                          outlined
                        ></v-autocomplete>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" sm="12" lg="12" md="12" id="step3">
                    <label class="field-label">
                      <v-icon small class="label-icon"
                        >mdi-map-marker-radius</v-icon
                      >
                      Venue Details <small>(Optional)</small>
                    </label>
                    <v-text-field
                      v-model="VenueDetails"
                      :rules="VenueDetailsRules"
                      hint="Enter the venue details"
                      outlined
                      dense
                    >
                      <template v-slot:prepend>
                        <v-icon color="#2B9EB3"> mdi-map-marker-radius </v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <br /><br />
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-bullseye-arrow</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">100% Efficiency Criteria</h3>
                  <p class="section-subtitle">
                    Select if event qualifies for 100% efficiency
                  </p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Choose the 100% efficiency criteria if this event is
                    eligible</span
                  >
                </v-tooltip>
              </div>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="12" lg="12" md="12" id="step7">
                    <label class="field-label">
                      <v-icon small class="label-icon"
                        >mdi-bullseye-arrow</v-icon
                      >
                      100% Efficiency
                    </label>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      v-model="EfficiencyId"
                      :rules="EfficiencyIdRules"
                      :items="EfficiencyIdOptions"
                      :loading="EfficiencyIdOptionsLoading"
                      dense
                      clearable
                      required
                      outlined
                      color="#3a67b1"
                    >
                      <template v-slot:prepend>
                        <v-icon color="#3a67b1">mdi-bullseye-arrow</v-icon>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <br /><br />
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-file-document</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">Event / Program / Activity</h3>
                  <p class="section-subtitle">
                    Select portfolio and event category
                  </p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Choose the portfolio and the suitable category under which
                    this event falls under</span
                  >
                </v-tooltip>
              </div>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="6" lg="4" md="4" id="step4">
                    <label class="field-label">
                      <v-icon small class="label-icon">mdi-folder-open</v-icon>
                      <span class="text-danger">*</span> Portfolio
                    </label>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      v-model="PortfolioId"
                      :rules="PortfolioIdRules"
                      :items="PortfolioIdOptions"
                      :loading="PortfolioIdOptionsLoading"
                      dense
                      clearable
                      required
                      outlined
                      color="#3a67b1"
                    >
                      <template v-slot:prepend>
                        <v-icon color="#3a67b1">mdi-file-document</v-icon>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6" lg="4" md="4" id="step5">
                    <label class="field-label">
                      <v-icon small class="label-icon"
                        >mdi-format-list-bulleted</v-icon
                      >
                      <span class="text-danger">*</span> Event Category
                    </label>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      v-model="EventCategoryId"
                      :rules="EventCategoryIdRules"
                      :items="EventCategoryIdOptions"
                      :loading="EventCategoryIdOptionsLoading"
                      dense
                      clearable
                      required
                      outlined
                      color="#3a67b1"
                    >
                      <template v-slot:prepend>
                        <v-icon color="#3a67b1"
                          >mdi-format-list-bulleted</v-icon
                        >
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6" lg="4" md="4">
                    <label class="field-label">
                      <v-icon small class="label-icon">mdi-tag-multiple</v-icon>
                      Event Sub Category <small>(Optional)</small>
                    </label>
                    <v-autocomplete
                      :reduce="(option) => option.value"
                      v-model="EventSubTypeId"
                      :rules="EventSubTypeIdRules"
                      :items="EventSubTypeIdOptions"
                      :loading="EventSubTypeIdOptionsLoading"
                      dense
                      clearable
                      required
                      outlined
                      color="#3a67b1"
                    >
                      <template v-slot:prepend>
                        <v-icon color="#3a67b1"
                          >mdi-format-list-bulleted</v-icon
                        >
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="12" lg="12" md="12" id="step6">
                    <label class="field-label">
                      <v-icon small class="label-icon">mdi-pencil</v-icon>
                      <span class="text-danger">*</span> Event Name
                    </label>
                    <v-text-field
                      v-model="EventName"
                      :rules="EventNameRules"
                      hint="Enter the event name"
                      outlined
                      dense
                      color="#3a67b1"
                    >
                      <template v-slot:prepend>
                        <v-icon color="#3a67b1">mdi-file-document-edit</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <br /><br />
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-image-multiple</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">Event Images</h3>
                  <p class="section-subtitle">
                    Upload invitation and event photo
                  </p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Upload the event invitation and a photo to be displayed in
                    JCI India website. Note: The photo is expected to be a
                    professional group photo only then it will be approved to be
                    displayed in JCI India website</span
                  >
                </v-tooltip>
              </div>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="6" lg="6" md="6">
                    <label class="field-label">
                      <v-icon small class="label-icon"
                        >mdi-card-bulleted-settings</v-icon
                      >
                      Invitation <small>(Optional)</small>
                    </label>
                    <v-file-input
                      :clearable="false"
                      truncate-length="25"
                      accept="image/png, image/jpeg, image/bmp"
                      placeholder="Upload Invitation"
                      v-model="InvitationImage.UploadedImage"
                      prepend-icon="mdi-card-bulleted-settings"
                      @change="addImage(1)"
                    >
                      <template v-slot:append-outer>
                        <v-icon color="red" @click="deleteImage(1)">
                          mdi-delete
                        </v-icon>
                      </template>
                    </v-file-input>
                    <!-- <input
                  type="file"
                  name="profile_avatar"
                  accept=".png, .jpg, .jpeg"
                  @change="loadImage($event, 1)"
                />
                <v-btn fab dark small color="blue" @click="deleteImage(1)">
                  <v-icon>mdi-upload</v-icon>
                </v-btn>
                <v-btn fab dark small color="red" @click="deleteImage(1)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn> -->
                  </v-col>
                  <v-col cols="12" sm="6" lg="6" md="6">
                    <label class="field-label">
                      <v-icon small class="label-icon"
                        >mdi-card-bulleted-settings</v-icon
                      >
                      Selected Invitation <small>(Optional)</small>
                    </label>
                    <v-img
                      :src="InvitationImage.ImageUrl"
                      height="100"
                      contain
                      v-if="InvitationImage.ImageUrl != ''"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" lg="6" md="6">
                    <h6 class="field-label">
                      <v-icon small class="label-icon">mdi-image</v-icon>
                      Event Photo for JCI India Website
                      <small>(Optional)</small>
                    </h6>
                    <v-file-input
                      :clearable="false"
                      truncate-length="25"
                      accept="image/png, image/jpeg, image/bmp"
                      placeholder="Upload Event Photo"
                      v-model="EventPhotoImage.UploadedImage"
                      prepend-icon="mdi-account-group"
                      @change="addImage(2)"
                    >
                      <template v-slot:append-outer>
                        <v-icon color="red" @click="deleteImage(2)">
                          mdi-delete
                        </v-icon>
                      </template>
                    </v-file-input>
                  </v-col>
                  <v-col cols="12" sm="6" lg="6" md="6">
                    <label class="field-label">
                      <v-icon small class="label-icon"
                        >mdi-image-outline</v-icon
                      >
                      Selected Event Photo <small>(Optional)</small>
                    </label>
                    <v-img
                      :src="EventPhotoImage.ImageUrl"
                      height="100"
                      contain
                      v-if="EventPhotoImage.ImageUrl != ''"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <br /><br />
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-account-tie</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">Event Chairman Details</h3>
                  <p class="section-subtitle">
                    Add event chairman (Applicable only for Zone/National
                    Events)
                  </p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Choose the event chairman by clicking the add icon
                    below</span
                  >
                </v-tooltip>
              </div>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="12" lg="12" md="12">
                    <div class="action-buttons">
                      <v-btn
                        icon
                        color="#0097d7"
                        @click.prevent="addChairmanRecordPrompt = true"
                        title="Add chairman"
                      >
                        <v-icon>mdi-plus-circle</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="#ef5350"
                        @click="deleteRow(3, 1, ChairmanData)"
                        title="Delete chairman"
                      >
                        <v-icon>mdi-delete-circle-outline</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
                <v-row wrap>
                  <v-col cols="12" sm="6" lg="4" md="4">
                    <label class="field-label">
                      <v-icon small class="label-icon"
                        >mdi-account-circle</v-icon
                      >
                      Event Chairman Photo
                    </label>
                    <br />
                    <img
                      :src="ChairmanData.MemberImage"
                      width="100"
                      class="table-image"
                      v-if="ChairmanData.Fullname != ''"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" lg="4" md="4">
                    <label class="field-label">
                      <v-icon small class="label-icon">mdi-information</v-icon>
                      Event Chairman Details
                    </label>
                    <br />
                    <h5 style="color: #3a67b1; font-weight: 600">
                      {{ ChairmanData.Fullname }}
                    </h5>
                    <h6 style="color: #414042">
                      {{ ChairmanData.MemberDesignation }}
                    </h6>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <br /><br />
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-account-star</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">
                    Program Chairman / Director Details
                  </h3>
                  <p class="section-subtitle">
                    Add program chairman or director details
                  </p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Choose the Program Chairman / Director by clicking the add
                    icon below</span
                  >
                </v-tooltip>
              </div>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="12" lg="12" md="12">
                    <div class="action-buttons">
                      <v-btn
                        icon
                        color="#0097d7"
                        @click.prevent="addDirectorRecordPrompt = true"
                        title="Add director"
                      >
                        <v-icon>mdi-plus-circle</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="#ef5350"
                        @click="deleteRow(4, 1, DirectorData)"
                        title="Delete director"
                      >
                        <v-icon>mdi-delete-circle-outline</v-icon>
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
                <v-row wrap>
                  <v-col cols="12" sm="6" lg="4" md="4">
                    <label class="field-label">
                      <v-icon small class="label-icon"
                        >mdi-account-circle</v-icon
                      >
                      Program Chairman / Director Photo
                    </label>
                    <br />
                    <img
                      :src="DirectorData.MemberImage"
                      width="100"
                      class="table-image"
                      v-if="DirectorData.Fullname != ''"
                    />
                  </v-col>
                  <v-col cols="12" sm="6" lg="4" md="4">
                    <label class="field-label">
                      <v-icon small class="label-icon">mdi-information</v-icon>
                      Program Chairman / Director Details
                    </label>
                    <br />
                    <h5 style="color: #3a67b1; font-weight: 600">
                      {{ DirectorData.Fullname }}
                    </h5>
                    <h6 style="color: #414042">
                      {{ DirectorData.MemberDesignation }}
                    </h6>
                  </v-col>
                </v-row>
              </v-container>
              <!-- <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="6" lg="3" md="3">
                    <label>
                      <h6>
                        Membership Id of event chairman
                        <small> (Optional)</small>
                      </h6>
                    </label>
                    <v-text-field
                      v-model="ChairmanMembershipId"
                      :rules="ChairmanMembershipIdRules"
                      hint="Enter the membership ID of event chairman"
                      :loading="ChairmanMembershipIdLoadingFlag"
                      append-outer-icon="mdi-magnify-plus"
                      @click:append-outer="getMemberDetails(1)"
                      prepend-icon="mdi-account-box"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" lg="3" md="3">
                    <label>
                      <h6>Event chairman details</h6>
                    </label>
                    <br />
                    <img
                      :src="ChairmanData.MemberImage"
                      width="100"
                      v-if="ChairmanData.Fullname != ''"
                    />
                    <h5>{{ ChairmanData.Fullname }}</h5>
                    <h6>{{ ChairmanData.MemberDesignation }}</h6>
                  </v-col>
                  <v-col cols="12" sm="6" lg="3" md="3">
                    <label>
                      <h6>
                        Select the project director
                        <small> (Optional)</small>
                      </h6>
                    </label>
                    <v-text-field
                      v-model="DirectorMembershipId"
                      :rules="DirectorMembershipIdRules"
                      hint="Membership id for event director"
                      append-outer-icon="mdi-magnify-plus"
                      :loading="DirectorMembershipIdLoadingFlag"
                      @click:append-outer="getMemberDetails(2)"
                      outlined
                      dense
                    ></v-text-field>
                    <v-autocomplete
                      @change="getDirectorMemberDetails"
                      :reduce="(option) => option.value"
                      :loading="MemberIdOptionsLoading"
                      :items="MemberIdOptions"
                      :rules="MemberIdRules"
                      v-model="MemberId"
                      prepend-icon="mdi-account-multiple-check"
                      append-outer-icon="mdi-refresh"
                      @click:append-outer="getMemberIdOptions"
                      required
                      outlined
                      dense
                      clearable
                    >
                      <template v-slot:selection="{ item }">
                        {{ item.text }}
                      </template>
                      <template v-slot:item="{ item }">
                        {{ item.text }}
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" sm="6" lg="3" md="3">
                    <label>
                      <h6>Event director details</h6>
                    </label>
                    <br />
                    <img
                      :src="DirectorData.MemberImage"
                      width="100"
                      v-if="DirectorData.Fullname != ''"
                    />
                    <h5>{{ DirectorData.Fullname }}</h5>
                    <h6>{{ DirectorData.MemberDesignation }}</h6>
                  </v-col>
                </v-row>
              </v-container> -->
            </v-card>
            <br /><br />
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-text-box-multiple</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">Describe About the Event</h3>
                  <p class="section-subtitle">
                    Write a compelling event description
                  </p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Write a brief description about the event. Should not
                    exceed 500 characters</span
                  >
                </v-tooltip>
              </div>
              <div class="section-note">
                <v-icon small>mdi-format-text</v-icon>
                <span
                  >Maximum: 500 characters | This field is optional but helps
                  others understand your event better</span
                >
              </div>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="12" lg="12" md="12" id="step8">
                    <label class="field-label">
                      <v-icon small class="label-icon"
                        >mdi-pencil-box-multiple</v-icon
                      >
                      Event Description <small>(Optional)</small>
                    </label>
                    <v-textarea
                      v-model="EventDescription"
                      value=""
                      :maxlength="500"
                      :counter="500"
                      outlined
                      dense
                      class="event-description-textarea"
                      placeholder="Describe your event in detail... (max 500 characters)"
                      color="#3a67b1"
                    ></v-textarea>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <br /><br />
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-image-gallery</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">Additional Event Images</h3>
                  <p class="section-subtitle">Add event photographs</p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span>Add event photographs by clicking the add icon</span>
                </v-tooltip>
              </div>
              <div class="section-note">
                <v-icon small>mdi-information</v-icon>
                <span
                  >Kindly note: Only JPEG, PNG file types are allowed. Max file
                  size: 1 MB per image.</span
                >
              </div>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="12" lg="12" md="12" id="step9">
                    <div class="table-wrapper">
                      <v-data-table
                        :headers="tableColumns2"
                        :items="AllDocuments"
                        hide-default-footer
                        disable-sort
                        id="document-table"
                        class="professional-table"
                      >
                        <template v-slot:no-data>
                          <div class="table-empty-state">
                            <v-icon size="48" color="#3a67b1"
                              >mdi-image-plus</v-icon
                            >
                            <p>No event images added yet</p>
                            <v-btn
                              icon
                              color="#3a67b1"
                              @click.prevent="addDocumentRecordPrompt = true"
                              class="add-btn"
                            >
                              <v-icon>mdi-plus-circle</v-icon>
                            </v-btn>
                          </div>
                        </template>
                        <template v-slot:item.ImageUrl="{ item }">
                          <v-img
                            :src="item.ImageUrl"
                            width="100"
                            height="100"
                            v-if="item.ImageUrl != ''"
                            class="table-image"
                          />
                        </template>
                        <template v-slot:item.action="{ item, index }">
                          <div class="action-buttons">
                            <v-btn
                              icon
                              small
                              color="#0097d7"
                              @click.prevent="addEventPhoto"
                              title="Add image"
                            >
                              <v-icon small>mdi-plus-circle</v-icon>
                            </v-btn>
                            <v-btn
                              icon
                              small
                              color="#ef5350"
                              @click="deleteRow(2, index, item)"
                              title="Delete image"
                            >
                              <v-icon small>mdi-delete-circle-outline</v-icon>
                            </v-btn>
                          </div>
                        </template>
                      </v-data-table>
                    </div>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <br /><br />
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-account-multiple</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">Guest Details</h3>
                  <p class="section-subtitle">
                    Add event dignitaries information
                  </p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span
                    >Add event dignitaries information by clicking the add
                    icon</span
                  >
                </v-tooltip>
              </div>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="12" lg="12" md="12" id="step10">
                    <div class="table-wrapper">
                      <v-data-table
                        :headers="tableColumns1"
                        :items="AllFaculty"
                        hide-default-footer
                        disable-sort
                        id="faculty-table"
                        class="professional-table"
                      >
                        <template v-slot:no-data>
                          <div class="table-empty-state">
                            <v-icon size="48" color="#3a67b1"
                              >mdi-account-plus</v-icon
                            >
                            <p>No guests added yet</p>
                            <v-btn
                              icon
                              color="#3a67b1"
                              @click.prevent="addFacultyRecordPrompt = true"
                              class="add-btn"
                            >
                              <v-icon>mdi-plus-circle</v-icon>
                            </v-btn>
                          </div>
                        </template>
                        <template v-slot:item.ImageUrl="{ item }">
                          <v-img
                            :src="item.ImageUrl"
                            width="100"
                            height="100"
                            v-if="item.ImageUrl != ''"
                            class="table-image"
                          />
                        </template>
                        <template v-slot:item.action="{ item, index }">
                          <div class="action-buttons">
                            <v-btn
                              icon
                              small
                              color="#0097d7"
                              @click.prevent="addFacultyRecordPrompt = true"
                              title="Add guest"
                            >
                              <v-icon small>mdi-plus-circle</v-icon>
                            </v-btn>
                            <v-btn
                              icon
                              small
                              color="#ef5350"
                              @click="deleteRow(1, index, item)"
                              title="Delete guest"
                            >
                              <v-icon small>mdi-delete-circle-outline</v-icon>
                            </v-btn>
                          </div>
                        </template>
                      </v-data-table>
                    </div>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <br /><br />
            <v-card elevation="8" class="form-card mb-6 section-event">
              <div class="section-header">
                <v-icon class="section-icon">mdi-file-pdf-box</v-icon>
                <div class="section-title-wrapper">
                  <h3 class="section-title">PDF File Upload</h3>
                  <p class="section-subtitle">Upload relevant documentation</p>
                </div>
                <v-spacer></v-spacer>
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" class="info-icon"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span>Upload relevant PDF document, if any</span>
                </v-tooltip>
              </div>
              <p class="notes-text1">
                Upload relavant PDF document, if any. <br />
                Note: Only PDF format is permitted with size not exceeding 5 MB
              </p>
              <v-container>
                <v-row wrap>
                  <v-col cols="12" sm="6" lg="6" md="6" id="step11">
                    <label class="field-label">
                      <v-icon small class="label-icon">mdi-file-pdf</v-icon>
                      Upload PDF <small>(Optional)</small>
                    </label>
                    <v-file-input
                      :clearable="false"
                      truncate-length="25"
                      accept="application/pdf"
                      placeholder="Upload pdf"
                      v-model="UploadedFile1"
                      prepend-icon="mdi-file-pdf"
                    >
                      <template v-slot:append-outer>
                        <v-icon color="red" @click="UploadedFile1 = {}">
                          mdi-delete
                        </v-icon>
                      </template>
                    </v-file-input>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
            <br /><br />
            <v-row wrap>
              <v-col align="center" cols="12" md="12">
                <upload-progress
                  :ProgessStart="progessStart"
                  :UploadPercentage="uploadPercentage"
                ></upload-progress>
                <br />
                <v-btn
                  id="step12"
                  type="submit"
                  @click.prevent="confirmAlert"
                  :loading="SubmitFlag"
                  class="submit-btn"
                >
                  <v-icon> mdi-briefcase-check </v-icon> &nbsp; Save Event
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import AddEventDocument from "@/view/pages/erp/events/AddEventDocument.vue";
import AddEventFaculty from "@/view/pages/erp/events/AddEventFaculty.vue";
import AddEventChairman from "@/view/pages/erp/events/AddEventChairman.vue";
import AddEventDirector from "@/view/pages/erp/events/AddEventDirector.vue";

export default {
  mixins: [common],
  components: {
    AddEventDocument,
    AddEventFaculty,
    AddEventChairman,
    AddEventDirector,
  },
  data() {
    return {
      TooltipShow1: false,

      TourOptions: {
        useKeyboardNavigation: false,
        labels: {
          buttonSkip: "Skip",
          buttonPrevious: "Previous",
          buttonNext: "Next",
          buttonStop: "Finish",
        },
      },
      TourSteps: [
        {
          target: "#step1",
          content:
            "Try it, you'll love it!<br>You can put HTML in the steps and completely customize the DOM to suit your needs.",
          params: {
            placement: "top",
          },
        },
        {
          target: "#step2",
          content:
            "Try it, you'll love it!<br>You can put HTML in the steps and completely customize the DOM to suit your needs.",
          params: {
            placement: "top",
          },
        },
        {
          target: "#step3",
          content:
            "Try it, you'll love it!<br>You can put HTML in the steps and completely customize the DOM to suit your needs.",
          params: {
            placement: "top",
          },
        },
      ],
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      OverlayLoadingFlag: false,
      SubmitFlag: false,
      SearchFlag: false,
      SearchResultFlag: false,
      ShowFormFlag: false,
      NewTitleFlag: true,
      NotesFlag: false,
      rows: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      CheckoutTableHeaders: [],

      tab: null,
      TabDisable1: false,
      TabDisable2: false,
      TabDisable3: false,

      TabFlag3: false,

      TotalAmount: 0,
      Contribution: [],

      TotalAmount1: 0,
      TotalAmount2: 0,

      Member: {},
      Order: {},
      MemberFlag: false,

      PenaltySubscription: [],
      EventQuantity: "",
      EventQuantityRules: [(v) => !!v || "Event quantity is required"],

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      progessStart: 0,
      uploadPercentage: 0,

      JciYearCode: "",

      DayTypeRules: [(v) => !!v || "Event day type is required"],
      DayType: "",
      DayTypeOptions: [
        { value: "", text: "- Select -" },
        { value: 1, text: "Single Day" },
        { value: 2, text: "Multiple Day" },
      ],
      DayTypeOptionsLoading: false,

      HostType: 1,
      HostTypeRules: [(v) => !!v || "Host type is required"],

      HostZoneCode: "",
      HostZoneCodeOptions: [],
      HostZoneCodeRules: [(v) => !!v || "Host Zone is required"],
      HostZoneCodeOptionsLoading: false,

      HostLomCode: "",
      HostLomCodeOptions: [],
      HostLomCodeRules: [(v) => !!v || "Host LO is required"],
      HostLomCodeOptionsLoading: false,

      EventTypeRules: [(v) => !!v || "Event type is required"],
      EventType: 3,
      EventTypeOptions: [
        { value: "", text: "- Select -" },
        { value: 1, text: "National Events" },
        { value: 2, text: "Zone Events" },
        { value: 3, text: "LOM Events" },
      ],
      EventTypeOptionsLoading: false,

      PortfolioIdRules: [(v) => !!v || "Portfolio is required"],
      PortfolioId: "",
      PortfolioIdOptions: [],
      PortfolioIdOptionsLoading: false,

      EfficiencyIdRules: [],
      EfficiencyId: "",
      EfficiencyIdOptions: [],
      EfficiencyIdOptionsLoading: false,

      EventCategoryIdRules: [(v) => !!v || "Event category is required"],
      EventCategoryId: "",
      EventCategoryIdOptions: [],
      EventCategoryIdOptionsLoading: false,

      EventSubTypeIdRules: [
        // (v) => !!v || "Event category is required"
      ],
      EventSubTypeId: "",
      EventSubTypeIdOptions: [],
      EventSubTypeIdOptionsLoading: false,

      ZoneName: "",

      ZoneCodeRules: [],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      LomName: "",

      LomCodeRules: [],
      LomCode: "",
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,

      EventName: "",
      EventNameRules: [(v) => !!v || "Event name is required"],

      FromDate: "",
      // FromDate: LuxonDateTime.local().plus({days: 0}).toISO(),
      FromDateRules: [(v) => !!v || "From date is required"],
      DateModal1: false,

      FromDateMin: "2022-01-01",
      FromDateMax: "2022-02-28",

      textFieldProps1: {
        prependIcon: "event",
        rules: [(v) => !!v || "From date is required"],
      },
      dateProps1: {
        headerColor: "#4285f4",
      },
      timeProps1: {
        useSeconds: false,
        ampmInTitle: true,
        scrollable: true,
        headerColor: "#4285f4",
        format: "ampm",
      },

      StartTime: null,
      TimeModal1: false,

      ToDate: "",
      ToDateRules: [(v) => !!v || "To date is required"],
      DateModal2: false,

      ToDateMin: "2022-01-01",
      ToDateMax: "2022-02-28",

      textFieldProps2: {
        prependIcon: "event",
        rules: [(v) => !!v || "To date is required"],
      },
      dateProps2: {
        headerColor: "#4285f4",
      },
      timeProps2: {
        useSeconds: false,
        ampmInTitle: true,
        scrollable: true,
        headerColor: "#4285f4",
        format: "ampm",
      },

      EndTime: null,
      TimeModal2: false,

      VenueDetails: "",
      VenueDetailsRules: [],

      ChairmanData: {},
      ChairmanMembershipId: "",
      ChairmanMembershipIdRules: [],
      ChairmanMembershipIdLoadingFlag: false,
      addChairmanRecordPrompt: false,

      DirectorData: {},
      DirectorMembershipId: "",
      DirectorMembershipIdRules: [],
      DirectorMembershipIdLoadingFlag: false,
      addDirectorRecordPrompt: false,

      EventDescription: "",
      EventDescriptionRules: [],

      AllFaculty: [],
      uploadedFacultyImages: [],
      tableColumns1: [
        { text: "Guest Type", value: "FacultyTypeName" },
        { text: "Member Type", value: "MemberTypeName" },
        { text: "Membership ID", value: "MembershipId" },
        { text: "Guest Name", value: "FacultyName" },
        { text: "Guest Details", value: "FacultyDetails" },
        { text: "Image", value: "ImageUrl" },
        { text: "Action", value: "action" },
      ],

      Faculty: {},
      addFacultyRecordPrompt: false,

      AllDocuments: [],
      uploadedEventImages: [],
      tableColumns2: [
        { text: "Document Type", value: "DocumentTypeName" },
        { text: "Description", value: "DocumentDescription" },
        { text: "Image", value: "ImageUrl" },
        { text: "Action", value: "action" },
      ],

      Document: {},
      addDocumentRecordPrompt: false,

      FacultyTypeIdRules: [(v) => !!v || "Faculty type is required"],
      FacultyTypeIdOptions: [],

      MemberTypeIdRules: [(v) => !!v || "Member type is required"],
      MemberTypeIdOptions: [
        { value: "", text: "- Select -" },
        { value: 1, text: "Member" },
        { value: 2, text: "Non Member" },
      ],

      MembershipIdRules: [],
      FacultyNameRules: [],
      FacultyDetailsRules: [],

      DocumentTypeIdRules: [(v) => !!v || "Document type is required"],
      DocumentTypeIdOptions: [],

      DocumentDescriptionRules: [],

      current_photo: null,

      MemberIdRules: [],
      MemberId: "",
      MemberIdOptions: [],
      MemberIdOptionsLoading: false,

      CurrentDesignation: "",
      MemeberFullname: "",

      image: {
        src: "",
        type: null,
      },

      InvitationImage: {},
      EventPhotoImage: {},
      EventPhotoImageLimit: 3,

      UploadedFile1: {},

      uploadedImages: [],
      TotalImages: 0,

      PageInfo: [],
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.JciYearCode = tr.CurrentJciYearId;
        var YearName = tr.CurrentJciYearName;
        this.YearName = YearName;

        var MonthCode = this.$route.query.month;
        MonthCode = MonthCode == (null || undefined) ? 0 : MonthCode;
        console.log({ MonthCode });
        this.MonthCode = MonthCode;

        var tempDate1 = YearName + "-" + MonthCode + "-01";
        console.log({ tempDate1 });

        var tempDate2 = new Date(tempDate1);
        console.log({ tempDate2 });
        var MonthName = tempDate2.toLocaleString("en-us", { month: "long" });
        console.log({ MonthName });
        this.MonthName = MonthName;

        var MrfStartDate = tr.MrfStartDate;
        console.log({ MrfStartDate });

        this.FromDateMin = MrfStartDate;
        this.ToDateMin = MrfStartDate;

        var MrfEndDate = tr.MrfEndDate;
        console.log({ MrfEndDate });

        this.FromDateMax = MrfEndDate;
        this.ToDateMax = MrfEndDate;

        this.pageData();
      }
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.LoadingFlag = false;
      this.ZoneCodeOptionsLoading = false;
    },
    ZoneCode: function () {
      console.log("watch ZoneCode");
      // this.getRegionCodeOptions();
      // this.LomCode = "";
      // this.getLomCodeOptions();
    },
    LomCodeOptions: function () {
      console.log("watch LomCodeOptions");
      this.LoadingFlag = false;
      this.LomCodeOptionsLoading = false;
    },
    PortfolioId: function () {
      console.log("watch PortfolioId");
      this.EventCategoryId = "";
      this.getEventCategoryIdOptions();
    },
    PortfolioIdOptions: function () {
      console.log("watch PortfolioIdOptions");
      this.LoadingFlag = false;
      this.PortfolioIdOptionsLoading = false;
    },
    EfficiencyIdOptions: function () {
      console.log("watch EfficiencyIdOptions");
      this.LoadingFlag = false;
      this.EfficiencyIdOptionsLoading = false;
    },
    EventCategoryId: function () {
      console.log("watch EventCategoryId");
      this.EventSubTypeId = "";
      this.getEventSubTypeIdOptions();
    },
    EventCategoryIdOptions: function () {
      console.log("watch EventCategoryIdOptions");
      this.LoadingFlag = false;
      this.EventCategoryIdOptionsLoading = false;
    },
    EventSubTypeIdOptions: function () {
      console.log("watch EventSubTypeIdOptions");
      this.LoadingFlag = false;
      this.EventSubTypeIdOptionsLoading = false;
    },
    ChairmanData: function () {
      console.log("watch ChairmanData");
      // this.LoadingFlag = false;
      // this.ChairmanMembershipIdLoadingFlag = false;
    },
    MemberIdOptions: function () {
      console.log("watch MemberIdOptions");
      this.MemberIdOptionsLoading = false;
    },
    InvitationImage: function () {
      console.log("watch InvitationImage");
      var file = this.InvitationImage.UploadedImage;
      console.log({ file });
      const src = this.InvitationImage.ImageUrl;
      console.log({ src });
      const type = this.InvitationImage.ImageType;
      console.log({ type });
    },
    HostZoneCodeOptions: function () {
      console.log("watch HostZoneCodeOptions");
      this.HostZoneCodeOptionsLoading = false;
    },
    HostLomCodeOptions: function () {
      console.log("watch HostLomCodeOptions");
      this.HostLomCodeOptionsLoading = false;
    },
    HostZoneCode: function () {
      console.log("watch HostZoneCode");
      this.getHostLomCodeOptions();
    },
  },
  methods: {
    backPage() {
      this.$router.go(-1);
    },
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    startTour() {
      console.log("startTour called");
      var TourFlag = this.PageInfo.TourFlag;
      console.log({ TourFlag });
      if (TourFlag == 1) {
        var TourSteps = this.PageInfo.TourSteps;
        console.log({ TourSteps });
        // this.$tours["myTour"].start();
        introJs()
          .setOptions({
            steps: [
              {
                element: document.querySelector("#step1"),
                intro: "Select whether the event is one day or multi day event",
              },
              /*
              {
                element: document.querySelector("#step2"),
                intro: "Select event end date",
              },
              {
                element: document.querySelector("#step3"),
                intro: "Select event venue",
              },
              */
              {
                element: document.querySelector("#step4"),
                intro: "Select the portfolio suitable for each event",
              },
              {
                element: document.querySelector("#step5"),
                intro: "Select the category suitable for each event",
              },
              {
                element: document.querySelector("#step6"),
                intro: "Type the name of your event here",
              },
              {
                element: document.querySelector("#step7"),
                intro: "Choose the 100% efficiency, if your event is eligible",
              },
              {
                element: document.querySelector("#step8"),
                intro:
                  "Write a brief description about the event not exceeding 250 characters",
              },
              {
                element: document.querySelector("#step9"),
                intro:
                  "Upload event images in this section. JPEG and PNG file types are only permitted. Each file should not be more than 1 MB.",
              },
              {
                element: document.querySelector("#step10"),
                intro:
                  "Enter the dignitaries details who participated in the event.",
              },
              {
                element: document.querySelector("#step11"),
                intro:
                  "Upload the pdf document in this section. This is optional.",
              },
              {
                element: document.querySelector("#step12"),
                intro: "Click here to submit the event.",
              },
            ],
          })
          .start();
      }
    },
    onFileChange(e) {
      const file = e.target.files[0];

      if (typeof FileReader === "function") {
        const reader = new FileReader();

        reader.onload = (event) => {
          this.current_photo = event.target.result;
        };

        reader.readAsDataURL(file);
      } else {
        alert("Sorry, FileReader API not supported");
      }
    },
    addEventPhoto() {
      var n2 = this.AllDocuments.length;
      console.log({ n2 });
      var m2 = this.EventPhotoImageLimit;
      console.log({ m2 });
      if (n2 < m2) {
        this.addDocumentRecordPrompt = true;
      } else {
        var message = "Event photos maximum limit " + m2 + " reached. ";
        this.sweetAlert("error", message, false);
      }
    },
    addImage(id) {
      console.log("addImage called");
      console.log({ id });
      switch (id) {
        case 1:
          var file = this.InvitationImage.UploadedImage;
          console.log({ file });
          var src = URL.createObjectURL(file);
          console.log({ src });
          if (this.InvitationImage.src) {
            URL.revokeObjectURL(this.InvitationImage.src);
          }
          var FileType = file.type;
          console.log({ FileType });
          this.InvitationImage = {
            DocumentFlag: true,
            DocumentTypeId: null,
            DocumentTypeName: null,
            DocumentDescription: null,
            ImageType: FileType,
            ImageUrl: src,
            UploadedImage: file,
          };
          break;

        case 2:
          var file = this.EventPhotoImage.UploadedImage;
          console.log({ file });
          var src = URL.createObjectURL(file);
          console.log({ src });
          if (this.EventPhotoImage.src) {
            URL.revokeObjectURL(this.EventPhotoImage.src);
          }
          var FileType = file.type;
          console.log({ FileType });
          this.EventPhotoImage = {
            DocumentFlag: true,
            DocumentTypeId: null,
            DocumentTypeName: null,
            DocumentDescription: null,
            ImageType: FileType,
            ImageUrl: src,
            UploadedImage: file,
          };
          break;

        case 3:
          var file = this.EventPhotoImage.UploadedImage;
          console.log({ file });
          var src = URL.createObjectURL(file);
          console.log({ src });
          if (this.EventPhotoImage.src) {
            URL.revokeObjectURL(this.EventPhotoImage.src);
          }
          var FileType = file.type;
          console.log({ FileType });
          this.EventPhotoImage = {
            DocumentFlag: true,
            DocumentTypeId: null,
            DocumentTypeName: null,
            DocumentDescription: null,
            ImageType: FileType,
            ImageUrl: src,
            UploadedImage: file,
          };
          break;

        default:
          break;
      }
    },
    deleteImage(id) {
      console.log("deleteImage called");
      console.log({ id });
      switch (id) {
        case 1:
          this.InvitationImage = {
            DocumentFlag: false,
            DocumentTypeId: null,
            DocumentTypeName: null,
            DocumentDescription: null,
            ImageType: null,
            ImageUrl: null,
            UploadedImage: null,
          };
          break;

        case 2:
          this.EventPhotoImage = {
            DocumentFlag: false,
            DocumentTypeId: null,
            DocumentTypeName: null,
            DocumentDescription: null,
            ImageType: null,
            ImageUrl: null,
            UploadedImage: null,
          };
          break;

        default:
          break;
      }
    },
    loadImage(event, k) {
      console.log("loadImage is called");
      console.log({ k });
      var n1 = this.TotalImages;
      console.log({ n1 });
      /*
      const file = event.target.files[0];
      if (typeof FileReader === "function") {
        const reader = new FileReader();

        reader.onload = (event) => {
          var result = event.target.result;
          const blob = URL.createObjectURL(file);
          console.log({ blob });
          this.uploadedImages[k] = {
            file: file,
            src: blob,
            type: null,
          };
          this.TotalImages = n1 + 1;
        };
        // reader.readAsDataURL(file);
      } else {
        console.log("Sorry, FileReader API not supported");
      }
      */

      const { files } = event.target;
      if (files && files[0]) {
        var file = event.target.files[0];
        const blob = URL.createObjectURL(files[0]);
        console.log({ blob });
        if (k == 1) {
          if (this.InvitationImage.src) {
            URL.revokeObjectURL(this.InvitationImage.src);
          }
          this.InvitationImage = {
            file: file,
            src: blob,
            type: null,
          };
        }
        /*
        var n1 = this.uploadedImages.length;
        console.log({ n1 });
        var images = this.uploadedImages;
        console.log({ images });
        var file = event.target.files[0];
        var src = n1 > k ? this.uploadedImages[k].src : null;
        this.TotalImages = n1 + 1;
        */
        // const reader = new FileReader();
        // reader.onload = (e) => {};
        // reader.readAsArrayBuffer(files[0]);
      }
    },
    addRecord() {
      console.log("addRecord called");
      var url = "/staff/list";
      var params = {};
      this.pageRedirect(params, url);
    },
    resetForm() {
      this.$refs.form1.reset();
      this.$refs.form2.reset();
      this.Member = {};
      this.MemberId = "";
      this.MembershipId = "";
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    resetMember() {
      console.log("resetMember called");
      this.LomCode = "";
      this.Member = {
        MemberId: "",
        PrimaryMobile: "",
        EmailId: "",
      };
      this.MemberFlag = false;
    },
    resetSubscription() {
      this.SearchResultFlag = false;
      this.rows = [];
      this.PenaltySubscription = [];
    },
    refreshPageData() {
      console.log("pageData called");
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "lom_events",
        Action: "create",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("refreshPageData called");

      var ZoneName = this.$session.get("ZoneName");
      ZoneName = ZoneName == (null || undefined) ? "" : ZoneName;
      console.log({ ZoneName });
      this.ZoneName = ZoneName;

      var ZoneCode = this.$session.get("ZoneId");
      ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
      console.log({ ZoneCode });
      this.ZoneCode = ZoneCode;

      var LomCode = this.$session.get("LomId");
      LomCode = LomCode == (null || undefined) ? "" : LomCode;
      console.log({ LomCode });
      this.LomCode = LomCode;

      var LomName = this.$session.get("LomName");
      LomName = LomName == (null || undefined) ? "" : LomName;
      console.log({ LomName });
      this.LomName = LomName;

      var CurrentDesignation = this.$session.get("CurrentDesignation");
      CurrentDesignation =
        CurrentDesignation == (null || undefined) ? "" : CurrentDesignation;
      console.log({ CurrentDesignation });
      this.CurrentDesignation = CurrentDesignation;

      var MemeberFullname = this.$session.get("MemeberFullname");
      MemeberFullname =
        MemeberFullname == (null || undefined) ? "" : MemeberFullname;
      console.log("MemeberFullname=" + MemeberFullname);
      this.MemeberFullname = MemeberFullname;

      this.getHostZoneCodeOptions();
      this.getZoneCodeOptions();
      this.getPortfolioIdOptions();
      this.getEfficiencyIdOptions();
      // this.getMemberIdOptions();
      // this.getFacultyTypeIdOptions();
      // this.getDocumentTypeIdOptions();
    },
    addFacultyImages(event, k) {
      console.log("addFacultyImages called");
      var n1 = this.uploadedFacultyImages.length;
      var file = event.target.files[0];
      console.log("k=" + k + ", event=" + JSON.stringify(file));
      this.uploadedFacultyImages[k] = file;
    },
    addEventImages(event, k) {
      console.log("addEventImages called");
      var n1 = this.uploadedEventImages.length;
      var file = event.target.files[0];
      console.log("k=" + k + ", event=" + JSON.stringify(file));
      this.uploadedEventImages[k] = file;
    },
    addNewFaculty(faculty) {
      console.log("addNewFaculty called");
      this.addFacultyRecordPrompt = false;
      console.log({ faculty });
      this.Faculty = faculty;
      this.addNewRow(1);
    },
    hideChairmanDialog(data) {
      console.log("hideChairmanDialog called");
      console.log({ data });
      var MembershipId = data.MembershipId;
      MembershipId = MembershipId == (null || undefined) ? "" : MembershipId;
      console.log({ MembershipId });
      this.ChairmanMembershipId = MembershipId;
      this.ChairmanData = data;
      this.addChairmanRecordPrompt = false;
    },
    hideDirectorDialog(data) {
      console.log("hideDirectorDialog called");
      console.log({ data });
      var MembershipId = data.MembershipId;
      MembershipId = MembershipId == (null || undefined) ? "" : MembershipId;
      console.log({ MembershipId });
      this.DirectorMembershipId = MembershipId;
      this.DirectorData = data;
      this.addDirectorRecordPrompt = false;
    },
    addNewDocument(document) {
      console.log("addNewDocument called");
      this.addDocumentRecordPrompt = false;
      console.log("document=" + JSON.stringify(document));
      this.Document = document;
      this.addNewRow(2);
    },
    addNewRow(tableId) {
      console.log("addNewRow called");

      console.log("tableId=" + tableId);

      switch (tableId) {
        case 1:
          var n1 = this.AllFaculty.length;
          console.log("n1=" + n1);
          var FacultyFlag = this.Faculty.FacultyFlag;
          console.log("FacultyFlag=" + FacultyFlag);
          if (FacultyFlag) {
            this.AllFaculty.push(this.Faculty);
          }
          this.Faculty = {};
          break;

        case 2:
          var n2 = this.AllDocuments.length;
          console.log("n2=" + n2);
          var DocumentFlag = this.Document.DocumentFlag;
          console.log("DocumentFlag=" + DocumentFlag);
          var m2 = this.EventPhotoImageLimit;
          console.log({ m2 });
          if (n2 < m2) {
            if (DocumentFlag) {
              this.AllDocuments.push(this.Document);
            }
          } else {
            var message = "Event photos maximum limit " + m2 + " reached. ";
            this.sweetAlert("error", message, false);
          }
          this.Document = {};
          break;

        default:
          break;
      }
    },
    deleteRow(tableId, index, tr) {
      console.log("deleteRow called");
      console.log({ tableId });
      console.log({ index });
      console.log({ tr });
      if (index >= 0) {
        switch (tableId) {
          case 1:
            this.AllFaculty.splice(index, 1);
            break;

          case 2:
            this.AllDocuments.splice(index, 1);
            break;

          case 3:
            this.ChairmanMembershipId = "";
            this.ChairmanData = {};
            break;

          case 4:
            this.DirectorMembershipId = "";
            this.DirectorData = {};
            break;

          default:
            break;
        }
      }
    },
    getPortfolioIdOptions() {
      console.log("getPortfolioIdOptions called");
      this.LoadingFlag = true;
      this.PortfolioIdOptionsLoading = true;
      var selectbox1_source = "PortfolioId";
      var selectbox1_destination = "PortfolioIdOptions";
      var selectbox1_url = "api/portfolio/options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getEfficiencyIdOptions() {
      console.log("getEfficiencyIdOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        this.LoadingFlag = true;
        this.EfficiencyIdOptionsLoading = true;
        var selectbox1_source = "EfficiencyId";
        var selectbox1_destination = "EfficiencyIdOptions";
        var selectbox1_url = "api/year-wise-efficiency/options";
        var selectbox1_conditions_array = {
          UserInterface: 3,
          Year: yearCode,
          Lom: this.LomCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getMemberIdOptions() {
      console.log("getMemberIdOptions called");
      this.MemberIdOptionsLoading = true;
      var selectbox1_source = "MemberId";
      var selectbox1_destination = "MemberIdOptions";
      var selectbox1_url = "api/members/options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
        LomCode: this.LomCode,
        MemberType: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getEventCategoryIdOptions() {
      console.log("getEventCategoryIdOptions called");
      var PortfolioId = this.PortfolioId;
      console.log("PortfolioId=" + PortfolioId);
      if (PortfolioId != "") {
        this.LoadingFlag = true;
        this.EventCategoryIdOptionsLoading = true;
        var selectbox1_source = "EventCategoryId";
        var selectbox1_destination = "EventCategoryIdOptions";
        var selectbox1_url = "api/jci-event-type/options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          Portfolio: PortfolioId,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (PortfolioId == "") {
          message += "Portfolio should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getEventSubTypeIdOptions() {
      console.log("getEventSubTypeIdOptions called");
      var EventCategoryId = this.EventCategoryId;
      console.log("EventCategoryId=" + EventCategoryId);
      if (EventCategoryId != "") {
        this.LoadingFlag = true;
        this.EventSubTypeIdOptionsLoading = true;
        var selectbox1_source = "EventSubTypeId";
        var selectbox1_destination = "EventSubTypeIdOptions";
        var selectbox1_url = "api/jci-event-sub-type/options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          JciEventType: EventCategoryId,
          NotAvailable: 1,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (EventCategoryId == "") {
          message += "Event type should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getHostZoneCodeOptions() {
      console.log("getHostZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        this.HostZoneCodeOptionsLoading = true;
        var selectbox1_source = "HostZoneCode";
        var selectbox1_destination = "HostZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getHostLomCodeOptions() {
      console.log("getHostLomCodeOptions called");
      var YearCode = this.JciYearCode;
      var HostZoneCode = this.HostZoneCode;
      this.HostLomCode = "";
      console.log("YearCode=" + YearCode + ", HostZoneCode=" + HostZoneCode);
      if (YearCode != "" && HostZoneCode != "") {
        this.HostLomCodeOptionsLoading = true;
        var selectbox1_source = "HostLomCode";
        var selectbox1_destination = "HostLomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 3,
          YearCode: YearCode,
          ZoneCode: HostZoneCode,
          AllOption: 0,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (HostZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        this.LoadingFlag = true;
        this.ZoneCodeOptionsLoading = true;
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getLomCodeOptions() {
      console.log("getLomCodeOptions called");
      var YearCode = this.JciYearCode;
      var ZoneCode = this.ZoneCode;
      console.log("YearCode=" + YearCode + ", ZoneCode=" + ZoneCode);
      if (YearCode != "" && ZoneCode != "") {
        this.LoadingFlag = true;
        this.LomCodeOptionsLoading = true;
        var selectbox1_source = "LomCode";
        var selectbox1_destination = "LomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 3,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
          AllOption: 0,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getFacultyTypeIdOptions() {
      console.log("getFacultyTypeIdOptions called");
      this.LoadingFlag = true;
      // this.PortfolioIdOptionsLoading = true;
      var selectbox1_source = "FacultyTypeId";
      var selectbox1_destination = "FacultyTypeIdOptions";
      var selectbox1_url = "api/sub-category/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        CategoryMainId: 17,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getDocumentTypeIdOptions() {
      console.log("getDocumentTypeIdOptions called");
      this.LoadingFlag = true;
      // this.PortfolioIdOptionsLoading = true;
      var selectbox1_source = "DocumentTypeId";
      var selectbox1_destination = "DocumentTypeIdOptions";
      var selectbox1_url = "api/sub-category/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        CategoryMainId: 16,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getDirectorMemberDetails() {
      console.log("getDirectorMemberDetails is called");
      this.DirectorData = {};
      this.DirectorMembershipId = "";
      var temp_code = this.MemberId;
      console.log("temp_code=" + temp_code);
      var idx = this.MemberIdOptions.map((e) => e.value).indexOf(temp_code);
      console.log("idx=" + idx);
      if (idx >= 0) {
        var member = this.MemberIdOptions[idx];
        console.log({ member });
        this.DirectorMembershipId = member.membership_id;
        this.DirectorData = {
          Fullname: member.member_name,
          MemberImage: member.photo,
          MemberDesignation: member.designation,
        };
      }
    },
    getMemberDetails(id) {
      console.log("getMemberDetails is called");

      var MembershipId = "";

      switch (id) {
        case 1:
          MembershipId = this.ChairmanMembershipId;
          this.ChairmanData = {};
          this.ChairmanMembershipIdLoadingFlag = true;
          break;
        case 2:
          MembershipId = this.DirectorMembershipId;
          this.DirectorData = {};
          this.DirectorMembershipIdLoadingFlag = true;
          break;

        default:
          break;
      }
      console.log("MembershipId=" + MembershipId);

      if (MembershipId != "") {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members/search";
        var upload = {
          UserInterface: 2,
          MembershipId: MembershipId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SubmitFlag = false;
            thisIns.StartupLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            switch (id) {
              case 1:
                thisIns.ChairmanMembershipIdLoadingFlag = false;
                thisIns.ChairmanData = records;
                break;
              case 2:
                thisIns.DirectorMembershipIdLoadingFlag = false;
                thisIns.DirectorData = records;
                break;

              default:
                break;
            }

            if (flag == 1) {
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.ChairmanMembershipIdLoadingFlag = false;
            thisIns.DirectorMembershipIdLoadingFlag = false;
          });
      } else {
        var message = "";
        this.ChairmanMembershipIdLoadingFlag = false;
        this.DirectorMembershipIdLoadingFlag = false;
        if (MembershipId == "") {
          message += "Membership id should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    confirmAlert() {
      console.log("confirmAlert called");
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        /*
        var MemberTypeId = this.MemberTypeId;
        console.log("MemberTypeId=" + MemberTypeId);

        // Get Title details

        var TitleId = this.TitleCode;
        var TitleIndex = this.TitleCodeOptions.map((e) => e.value).indexOf(
          TitleId
        );
        console.log("TitleId=" + TitleId + ", TitleIndex=" + TitleIndex);
        var TitleName = this.TitleCodeOptions[TitleIndex].text;
        console.log("TitleName=" + TitleName);
        */
        var htmlTxt = "";
        // htmlTxt += "<br/> Title <b> " + TitleName + "</b>";

        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      // validate1 = false;

      if (validate1) {
        this.SubmitFlag = true;
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-events/store";
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        var MemberTypeId1 = this.MemberTypeId;
        var JciMemberTypeId = this.JciMemberTypeId;
        var MemberTypeId = MemberTypeId1 == JciMemberTypeId ? 1 : MemberTypeId1;
        console.log(
          "MemberTypeId1=" +
            MemberTypeId1 +
            ", JciMemberTypeId=" +
            JciMemberTypeId +
            ", MemberTypeId=" +
            MemberTypeId
        );

        var EventData = {
          Year: this.JciYearCode,
          DayType: this.DayType,
          EventType: this.EventType,
          HostType: this.HostType ? 1 : 0,
          HostZone: this.HostType == 1 ? null : this.HostZoneCode,
          HostLO: this.HostType == 1 ? null : this.HostLomCode,
          Portfolio: this.PortfolioId,
          EfficiencyCriteria: this.EfficiencyId,
          EventCategory: this.EventCategoryId,
          EventSubType: this.EventSubTypeId,
          Zone: this.ZoneCode,
          Lom: this.LomCode,
          EventName: this.EventName,
          EventDescription: this.EventDescription,
          ChairmanMembershipId: this.ChairmanMembershipId,
          ChairmanName: this.ChairmanData.Fullname,
          ChairmanDetails: this.ChairmanData.MemberDesignation,
          DirectorMembershipId: this.DirectorMembershipId,
          DirectorName: this.DirectorData.Fullname,
          DirectorDetails: this.DirectorData.MemberDesignation,
          FromDate: this.FromDate,
          StartTime: this.StartTime,
          ToDate: this.ToDate,
          EndTime: this.EndTime,
          VenueDetails: this.VenueDetails,
          Faculty: this.AllFaculty,
          Documents: this.AllDocuments,
          ChairmanData: this.ChairmanData,
          DirectorData: this.DirectorData,
          Quantity: this.EventQuantity,
        };

        console.log({ EventData });

        console.table(this.ChairmanData);
        console.table(this.DirectorData);

        let upload = new FormData();
        upload.append("UserInterface", 1);
        upload.append("Form", JSON.stringify(EventData));

        var ChairmanImageName = this.ChairmanData.UploadedImageName;
        console.log({ ChairmanImageName });

        ChairmanImageName =
          ChairmanImageName == (null || undefined) ? "" : ChairmanImageName;

        if (ChairmanImageName != "") {
          upload.append(
            "ChairmanImage",
            this.ChairmanData.UploadedImage,
            ChairmanImageName
          );
        }

        var DirectorImageName = this.DirectorData.UploadedImageName;
        console.log({ DirectorImageName });

        DirectorImageName =
          DirectorImageName == (null || undefined) ? "" : DirectorImageName;

        if (DirectorImageName != "") {
          upload.append(
            "DirectorImage",
            this.DirectorData.UploadedImage,
            DirectorImageName
          );
        }

        upload.append("InvitationImage", this.InvitationImage.UploadedImage);
        upload.append(
          "InvitationDescription",
          this.InvitationImage.DocumentDescription
        );

        upload.append("EventPhotoImage", this.EventPhotoImage.UploadedImage);
        upload.append(
          "EventPhotoDescription",
          this.EventPhotoImage.DocumentDescription
        );
        upload.append("UploadedFile1", this.UploadedFile1);

        var n1 = this.AllFaculty.length;
        var m1 = 0;
        for (var i = 0; i < n1; i++) {
          var uploadedImageFlag = this.AllFaculty[i].FacultyFlag;
          console.log(
            "i=" + i + ", m1=" + m1 + ", uploadedImageFlag=" + uploadedImageFlag
          );
          if (uploadedImageFlag) {
            var uploadedImageId = "FacultyImages" + m1;
            var tempDocument = this.AllFaculty[i];
            var uploadedImageFile = tempDocument.UploadedImage;
            var UploadedImageName = tempDocument.UploadedImageName;
            console.log({ tempDocument });
            console.log({ uploadedImageFile });
            console.log({ UploadedImageName });
            if (uploadedImageFile != null) {
              upload.append(
                uploadedImageId,
                uploadedImageFile,
                UploadedImageName
              );
            }
            // var uploadedFile = this.$refs[refName].files[0]
            m1++;
          }
        }

        var n2 = this.AllDocuments.length;
        var m2 = 0;
        for (var j = 0; j < n2; j++) {
          var uploadedImageFlag = this.AllDocuments[j].DocumentFlag;
          console.log(
            "j=" + j + ", m2=" + m2 + ", uploadedImageFlag=" + uploadedImageFlag
          );
          if (uploadedImageFlag) {
            var uploadedImageId = "EventImages" + m2;
            var tempDocument = this.AllDocuments[j];
            var uploadedImageFile = tempDocument.UploadedImage;
            var UploadedImageName = tempDocument.UploadedImageName;
            console.log({ tempDocument });
            // var uploadedFile = this.$refs[refName].files[0]
            upload.append(
              uploadedImageId,
              uploadedImageFile,
              UploadedImageName
            );
            m2++;
          }
        }
        /*
        var upload = {
          member: member,
        };
        */
        console.log("upload=" + JSON.stringify(upload));

        this.progessStart = 1;

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;

            thisIns.progessStart = 0;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              // thisIns.toast("success", output);
              thisIns.sweetAlert("success", output, true);
              // thisIns.ShowFormFlag = false;
              // thisIns.resetForm();
              thisIns.$router.push("/lom-events/month-list");
            } else {
              // thisIns.toast("error", output);
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.progessStart = 0;
            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields";
        }
        // this.toast("error", message);
        this.sweetAlert("error", output, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
    /*
    this.tableColumns1.push(
      { text: "Description", value: "Description" },
      { text: "Subscription Fees", value: "AmountTxt" },
      { text: "Nos", value: "Qty" },
      { text: "Sub Total", value: "SubTotal" }
      // { text: "Actions", value: "actions" }
    );
    */
  },
  mounted: function () {
    // this.$tours["myTour"].start();
    // this.startTour();
  },
};
</script>
<style lang="scss">
// JCI Approved Color Palette
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;

$primary-color: $navy;
$secondary-color: $aqua;
$success-color: $seaform;
$warning-color: $gold;
$danger-color: #e74c3c;
$info-color: $aqua;
$light-bg: #f8fafc;
$bg-light: #f1f5f9;
$text-primary: $text-off-black;
$text-secondary: $text-90-black;
$border-color: #e2e8f0;
$gradient-main: linear-gradient(135deg, $navy 0%, $aqua 100%);

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-12px);
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

#lom-events-create {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: linear-gradient(135deg, $aqua 0%, $navy 100%) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
          circle at 20% 50%,
          rgba(237, 190, 56, 0.1) 0%,
          transparent 50%
        ),
        radial-gradient(
          circle at 80% 80%,
          rgba(86, 189, 163, 0.1) 0%,
          transparent 50%
        );
      pointer-events: none;
    }
  }

  .header-gradient {
    padding: 0;
  }

  .header-container {
    padding: 20px 0 !important;
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
    flex-shrink: 0;
  }

  .header-title-bar {
    flex: 1;
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideDown 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0;
  }

  .header-subtitle {
    font-size: 16px;
    opacity: 0.98;
    animation: slideDown 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
    margin: 8px 0 0 0;
  }

  .action-btn {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;
    border-radius: 12px;

    &:hover {
      background: rgba(255, 255, 255, 0.25) !important;
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
      transform: translateY(-2px);
    }
  }

  .gap-2 {
    gap: 8px;
  }

  // ============================================
  // WELCOME CARD - PREMIUM DESIGN
  // ============================================
  .welcome-card {
    background: linear-gradient(
      135deg,
      rgba(58, 103, 177, 0.95) 0%,
      rgba(0, 151, 215, 0.95) 100%
    ) !important;
    border-radius: 12px !important;
    overflow: hidden;
    animation: slideInUp 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    box-shadow: 0 8px 24px rgba(58, 103, 177, 0.15),
      0 0 20px rgba(0, 151, 215, 0.08);
    border: 1px solid rgba(58, 103, 177, 0.2) !important;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      box-shadow: 0 24px 80px rgba(58, 103, 177, 0.5),
        0 0 60px rgba(0, 151, 215, 0.4);
      transform: translateY(-4px);
      border-color: rgba(255, 255, 255, 0.25) !important;
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
          circle at 20% 50%,
          rgba(237, 190, 56, 0.15) 0%,
          transparent 50%
        ),
        radial-gradient(
          circle at 80% 80%,
          rgba(86, 189, 163, 0.15) 0%,
          transparent 50%
        );
      pointer-events: none;
    }
  }

  .welcome-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 15% 30%,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 40%
      ),
      radial-gradient(
        circle at 85% 70%,
        rgba(255, 255, 255, 0.05) 0%,
        transparent 40%
      );
    pointer-events: none;
  }

  .welcome-card-content {
    position: relative;
    z-index: 1;
  }

  .welcome-icon-box {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border-radius: 14px;
    padding: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.25);
    animation: float 4s ease-in-out infinite;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 56px;
    min-height: 56px;
  }

  .welcome-greeting-text {
    font-size: 18px;
    font-weight: 800;
    color: $text-white;
    letter-spacing: 0.3px;
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.2);
    margin-bottom: 6px;
    line-height: 1.2;
  }

  .welcome-name {
    background: linear-gradient(135deg, $gold 0%, #ffd966 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 900;
    letter-spacing: 1px;
  }

  .welcome-role,
  .welcome-lom {
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    line-height: 1.3;

    .role-icon {
      color: $gold;
    }
  }

  .welcome-badge {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border-radius: 50%;
    width: 52px;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.25);
    animation: pulse 2s ease-in-out infinite;
  }

  // ============================================
  // OLD WELCOME STYLES (KEEP FOR COMPATIBILITY)
  // ============================================
  .welcome-text {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 18px;
    font-weight: 600;
    color: $navy;
  }

  .welcome-subtitle {
    font-size: 14px;
    color: $text-90-black;
    margin-top: 8px;
    font-weight: 500;
  }

  @keyframes pulse {
    0%,
    100% {
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 0 0 0 rgba(237, 190, 56, 0.7);
    }

    50% {
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 0 0 15px rgba(237, 190, 56, 0);
    }
  }

  // ============================================
  // DESCRIPTION CARD - EVENT/ACTIVITY DESIGN
  // ============================================
  .description-card {
    border-radius: 24px !important;
    border: 3px solid rgba(58, 103, 177, 0.25) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.2),
      0 0 40px rgba(0, 151, 215, 0.12) !important;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.98) 0%,
      rgba(240, 248, 255, 0.95) 100%
    ) !important;
    animation: fadeInScale 0.6s ease-out;
    position: relative;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 6px;
      background: linear-gradient(90deg, $navy 0%, $aqua 50%, $seaform 100%);
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
          circle at 0% 100%,
          rgba(237, 190, 56, 0.05) 0%,
          transparent 50%
        ),
        radial-gradient(
          circle at 100% 0%,
          rgba(0, 151, 215, 0.05) 0%,
          transparent 50%
        );
      pointer-events: none;
    }

    &:hover {
      border-color: rgba(58, 103, 177, 0.4) !important;
      box-shadow: 0 24px 60px rgba(58, 103, 177, 0.25),
        0 0 50px rgba(0, 151, 215, 0.15) !important;
      transform: translateY(-6px);
    }
  }

  .desc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 28px 32px;
    background: linear-gradient(
      135deg,
      rgba(58, 103, 177, 0.08) 0%,
      rgba(0, 151, 215, 0.08) 100%
    );
    border-bottom: 3px solid rgba(58, 103, 177, 0.15);
    position: relative;
  }

  .desc-icon {
    color: $navy;
    font-size: 38px;
    flex-shrink: 0;
    filter: drop-shadow(0 2px 8px rgba(58, 103, 177, 0.2));
  }

  .desc-title {
    font-size: 22px;
    font-weight: 900;
    color: $navy;
    margin: 0;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    flex: 1;
  }

  .desc-divider {
    opacity: 0.3;
  }

  .desc-content {
    padding: 32px;
    position: relative;
    z-index: 2;
  }

  .desc-text {
    font-size: 16px;
    color: $text-primary;
    line-height: 1.8;
    margin: 0 0 24px 0;
    font-weight: 500;

    .required-indicator {
      color: #ef5350;
      font-weight: 900;
      margin: 0 4px;
    }
  }

  .desc-features {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 16px;
    margin-top: 24px;
  }

  .feature-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    background: linear-gradient(
      135deg,
      rgba(86, 189, 163, 0.12) 0%,
      rgba(0, 151, 215, 0.12) 100%
    );
    border-radius: 16px;
    border-left: 5px solid $seaform;
    border: 2px solid rgba(86, 189, 163, 0.2);
    border-left: 5px solid $seaform;
    font-size: 15px;
    font-weight: 700;
    color: $navy;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 16px rgba(86, 189, 163, 0.08);

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(86, 189, 163, 0.18) 0%,
        rgba(0, 151, 215, 0.18) 100%
      );
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(86, 189, 163, 0.15);
    }

    .feature-icon {
      color: $seaform;
      flex-shrink: 0;
      font-size: 24px;
    }
  }

  // ============================================
  // FORM CARDS & SECTIONS
  // ============================================
  .form-card {
    border-radius: 18px !important;
    border: 2px solid rgba(58, 103, 177, 0.2);
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.15),
      0 0 30px rgba(0, 151, 215, 0.08);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    animation: fadeInScale 0.6s ease-out;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(245, 250, 255, 0.9) 100%
    );
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, $navy 0%, $aqua 100%);
    }

    &:hover {
      border-color: rgba(0, 151, 215, 0.4);
      box-shadow: 0 16px 60px rgba(58, 103, 177, 0.25),
        0 0 50px rgba(0, 151, 215, 0.15);
      transform: translateY(-4px);
    }
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: linear-gradient(
      135deg,
      rgba(58, 103, 177, 0.05) 0%,
      rgba(0, 151, 215, 0.05) 100%
    );
    border-bottom: 2px solid rgba(58, 103, 177, 0.1);
  }

  .section-icon {
    color: $navy;
    font-size: 28px;
    flex-shrink: 0;
  }

  .section-title-wrapper {
    flex: 1;
  }

  .section-title {
    font-size: 18px;
    font-weight: 800;
    color: $navy;
    margin: 0;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  .section-subtitle {
    font-size: 13px;
    color: $text-90-black;
    margin: 4px 0 0 0;
    font-weight: 500;
  }

  .info-icon {
    color: $gold;
    cursor: help;
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.1);
      color: $warning-color;
    }
  }

  .section-note {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 20px;
    background: rgba(0, 151, 215, 0.08);
    border-left: 4px solid $aqua;
    color: $text-90-black;
    font-size: 13px;
    font-weight: 500;
  }

  // ============================================
  // FIELD LABELS
  // ============================================
  .field-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    color: $navy;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
  }

  .label-icon {
    color: $aqua;
  }

  .text-danger {
    color: #ef5350;
    font-weight: 900;
  }

  // ============================================
  // EVENT DESCRIPTION TEXTAREA
  // ============================================
  ::v-deep .event-description-textarea {
    .v-text-field__base {
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98) 0%,
        rgba(240, 248, 255, 0.98) 100%
      ) !important;
    }

    textarea {
      font-size: 15px !important;
      line-height: 1.8 !important;
      font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif !important;
      color: $text-primary !important;
      padding: 16px !important;
      resize: vertical;
      min-height: 160px !important;
    }

    .v-field__outline {
      border-color: rgba(58, 103, 177, 0.3) !important;
      border-width: 2px !important;
    }

    .v-field:hover .v-field__outline {
      border-color: rgba(58, 103, 177, 0.5) !important;
    }

    .v-field.v-field--focused .v-field__outline {
      border-color: $navy !important;
      border-width: 2px !important;
      box-shadow: 0 0 0 3px rgba(58, 103, 177, 0.1);
    }

    .v-counter {
      font-size: 13px;
      color: $text-secondary;
      font-weight: 600;
      margin-top: 8px;
    }

    .v-field {
      border-radius: 12px;
      overflow: hidden;
    }
  }

  // ============================================
  // PROFESSIONAL TABLE
  // ============================================
  .table-wrapper {
    border-radius: 16px;
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.2),
      0 0 30px rgba(0, 151, 215, 0.1);
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(245, 250, 255, 0.95) 100%
    );
    border: 2px solid rgba(58, 103, 177, 0.15);
    animation: fadeInScale 0.7s ease-out;
    position: relative;
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
      border-color: rgba(0, 151, 215, 0.3);
      box-shadow: 0 16px 50px rgba(58, 103, 177, 0.25),
        0 0 40px rgba(0, 151, 215, 0.15);
    }
  }

  .professional-table {
    background: transparent;

    thead tr {
      background: linear-gradient(90deg, $navy 0%, $aqua 100%);
      border: none;

      th {
        color: $text-white !important;
        font-weight: 800;
        font-size: 12px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        padding: 16px 12px !important;
        border: none;
      }
    }

    tbody tr {
      border-bottom: 1px solid rgba(58, 103, 177, 0.1);
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(0, 151, 215, 0.05);
        box-shadow: inset 0 0 10px rgba(0, 151, 215, 0.05);
      }

      td {
        font-size: 14px;
        color: $text-primary;
        padding: 14px 12px !important;
        font-weight: 500;
      }
    }
  }

  .table-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 50px 24px;
    gap: 16px;
    background: linear-gradient(
      135deg,
      rgba(58, 103, 177, 0.03) 0%,
      rgba(0, 151, 215, 0.03) 100%
    );
    border-radius: 14px;
    border: 3px dashed rgba(58, 103, 177, 0.2);
    animation: fadeInScale 0.6s ease-out;

    .empty-icon {
      color: $navy;
      opacity: 0.6;
    }

    p {
      font-size: 15px;
      font-weight: 600;
      color: $text-90-black;
      margin: 0;
      text-align: center;
    }

    .add-btn {
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.1);
      }
    }
  }

  .action-buttons {
    display: flex;
    gap: 6px;
    align-items: center;
    justify-content: flex-start;

    .v-btn {
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.15);
      }
    }
  }

  .table-image {
    border-radius: 8px;
    border: 2px solid rgba(58, 103, 177, 0.2);
    transition: all 0.3s ease;

    &:hover {
      border-color: $aqua;
      box-shadow: 0 4px 12px rgba(0, 151, 215, 0.2);
      transform: scale(1.05);
    }
  }

  // ============================================
  // SUBMIT BUTTON
  // ============================================
  .submit-btn {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    color: $text-white !important;
    font-weight: 800;
    font-size: 16px;
    padding: 14px 48px !important;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.3) !important;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    border: 2px solid transparent;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, $aqua 0%, $navy 100%) !important;
      box-shadow: 0 16px 60px rgba(0, 151, 215, 0.4) !important;
      transform: translateY(-2px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  // ============================================
  // PAGE CONTENT
  // ============================================
  .page-content {
    padding: 20px;
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    .header-card {
      margin-bottom: 20px;
    }

    .form-card {
      margin-bottom: 20px;
    }
  }

  @media (max-width: 600px) {
    .header-title {
      font-size: 24px;
    }

    .header-subtitle {
      font-size: 13px;
    }

    .action-btn {
      padding: 6px !important;
    }

    .form-card {
      border-radius: 12px;
      padding: 12px;
    }

    .section-header {
      padding: 12px;
      flex-direction: column;
      align-items: flex-start;
    }

    .section-icon {
      font-size: 24px;
    }

    .section-title {
      font-size: 16px;
    }

    .table-wrapper {
      border-radius: 12px;
    }

    .table-empty-state {
      padding: 30px 16px;
    }
  }
}
</style>